<template>
  <div>
    <div
      data-testid="tm-report-drilldown__toolbar"
      class="consolidated-results__toolbar tm-report-drilldown__toolbar"
    >
      <div class="consolidated-results__toolbar-left tm-report-drilldown__toolbar-left">
        <button
          type="button"
          data-testid="tm-report-drilldown__back-btn"
          class="tm-report-drilldown__back-btn"
          @click="$emit('back')"
        >
          <v-icon small class="mr-1">mdi-arrow-left</v-icon>
          <span>Back</span>
        </button>

        <button
          type="button"
          data-testid="tm-report-drilldown__filter-btn"
          class="consolidated-results__action-btn"
          @click="$emit('open-filter')"
        >
          <img src="/img/icons/fbc-icons/filter.svg" alt="Filter" class="consolidated-results__action-icon" />
          <span>Filters</span>
          <span
            v-if="activeFilterCount > 0"
            data-testid="tm-report-drilldown__filter-badge"
            class="consolidated-results__badge"
          >
            {{ activeFilterCount }}
          </span>
        </button>

        <button
          type="button"
          data-testid="tm-report-drilldown__sort-btn"
          class="consolidated-results__action-btn"
          @click="$emit('open-sort')"
        >
          <img src="/img/icons/fbc-icons/sort.svg" alt="Sort" class="consolidated-results__action-icon" />
          <span>Sort</span>
          <span
            v-if="activeSortCount > 0"
            data-testid="tm-report-drilldown__sort-badge"
            class="consolidated-results__badge"
          >
            {{ activeSortCount }}
          </span>
        </button>

        <div class="count-card gap-8" data-testid="tm-report-drilldown__total-amount-card">
          <h6 class="count-card__label font-weight-medium mr-3">Total Amount</h6>
          <h4 class="count-card__value font-weight-medium">{{ formattedSummaryAmount }}</h4>
        </div>

        <div class="count-card gap-8" data-testid="tm-report-drilldown__total-records-card">
          <h6 class="count-card__label font-weight-medium mr-3">Total Records</h6>
          <h4 class="count-card__value font-weight-medium">{{ formatNumber(totalCount) }}</h4>
        </div>

        <v-btn
          icon
          small
          title="Refresh data"
          data-testid="tm-report-drilldown__refresh-btn"
          @click="$emit('refresh')"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div class="consolidated-results__toolbar-right">
        <DownloadExcel
          class="consolidated-results__download-excel-v2"
          button-text="Generate Excel"
          button-color="white"
          test-id-prefix="tm-report-drilldown__download-excel"
          :download-history="downloadHistory"
          :loading-history="loadingHistory"
          :current-user-id="$store.state.user ? String($store.state.user.id || $store.state.user._id || '') : ''"
          :compact="true"
          :total-count="exportTotalCount"
          :max-export-records="maxExportRecords"
          :disabled="loading"
          @generate="$emit('generate-excel')"
          @refresh="$emit('refresh-download-history')"
          @download="$emit('download-history-file', $event)"
        />

        <div v-if="pageCount > 1" class="consolidated-results__toolbar-divider" />

        <div
          v-if="pageCount > 1"
          data-testid="tm-report-drilldown__pagination"
          class="consolidated-results__pagination-v2"
        >
          <button
            data-testid="tm-report-drilldown__prev-button"
            :disabled="page === 1"
            class="consolidated-results__pagination-v2-btn"
            @click="$emit('page-change', page - 1)"
          >
            <v-icon :size="15">mdi-chevron-left</v-icon>
          </button>
          <span data-testid="tm-report-drilldown__page-info" class="consolidated-results__page-info-v2">
            Page <span class="font-weight-bold">{{ page }}</span> of <span class="font-weight-bold">{{ formatNumber(pageCount) }}</span>
          </span>
          <button
            data-testid="tm-report-drilldown__next-button"
            :disabled="page === pageCount"
            class="consolidated-results__pagination-v2-btn"
            @click="$emit('page-change', page + 1)"
          >
            <v-icon :size="15">mdi-chevron-right</v-icon>
          </button>

          <div data-testid="tm-report-drilldown__page-jump" class="consolidated-results__page-jump-v2">
            <input
              v-model.number="pageJumpInput"
              type="text"
              data-testid="tm-report-drilldown__page-jump-input"
              class="consolidated-results__page-jump-v2-input"
              @keyup.enter="jumpToPage"
            />
            <button
              type="button"
              data-testid="tm-report-drilldown__page-jump-btn"
              class="consolidated-results__page-jump-v2-btn"
              :disabled="!isValidPageJump"
              @click="jumpToPage"
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>

    <TMResultsSortDropdown
      v-if="showSortModal"
      test-id-prefix="tm-report-drilldown"
      sort-btn-test-id="tm-report-drilldown__sort-btn"
      :active-sort-tab="activeSortTab"
      :available-sort-columns="availableSortColumns"
      :saved-sort="savedSort"
      :playground-sort="playgroundSort"
      :enable-saved-filter-sort="enableSavedFilterSort"
      @close="$emit('close-sort')"
      @update:active-sort-tab="$emit('update:active-sort-tab', $event)"
      @toggle-sort-column="$emit('toggle-sort-column', $event)"
      @set-sort-direction="(key, direction) => $emit('set-sort-direction', key, direction)"
      @reset-sort="$emit('reset-sort')"
      @apply-sort="$emit('apply-sort')"
    />
  </div>
</template>

<script>
import DownloadExcel from '../TransationMatching/downloadExcel.vue';
import TMResultsSortDropdown from '../shared/TMResultsSortDropdown.vue';
import { formatNumber, MAX_EXCEL_EXPORT_RECORDS } from '../shared/tmResultsDataTableUtils.js';

export default {
  name: 'TMReportDrilldownToolbar',

  components: {
    DownloadExcel,
    TMResultsSortDropdown,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    summaryAmount: {
      type: Number,
      default: null,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageCount: {
      type: Number,
      default: 0,
    },
    activeFilterCount: {
      type: Number,
      default: 0,
    },
    activeSortCount: {
      type: Number,
      default: 0,
    },
    downloadHistory: {
      type: Array,
      default: () => [],
    },
    loadingHistory: {
      type: Boolean,
      default: false,
    },
    showSortModal: {
      type: Boolean,
      default: false,
    },
    activeSortTab: {
      type: String,
      default: 'saved',
    },
    availableSortColumns: {
      type: Array,
      default: () => [],
    },
    savedSort: {
      type: Array,
      default: () => [],
    },
    playgroundSort: {
      type: Array,
      default: () => [],
    },
    enableSavedFilterSort: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      pageJumpInput: null,
      maxExportRecords: MAX_EXCEL_EXPORT_RECORDS,
    };
  },

  computed: {
    exportTotalCount() {
      if (this.loading) return 0;
      return this.totalCount;
    },

    formattedSummaryAmount() {
      if (this.summaryAmount === null || this.summaryAmount === undefined) return '–';
      return this.$nova.formatNumber(this.summaryAmount);
    },

    isValidPageJump() {
      return (
        this.pageJumpInput !== null
        && this.pageJumpInput >= 1
        && this.pageJumpInput <= this.pageCount
        && this.pageJumpInput !== this.page
      );
    },
  },

  methods: {
    formatNumber(num) {
      return formatNumber(num);
    },

    jumpToPage() {
      if (this.isValidPageJump) {
        this.$emit('page-change', this.pageJumpInput);
        this.pageJumpInput = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tm-report-drilldown__toolbar-left {
  flex-wrap: wrap;
  gap: var(--space-2);
}

.tm-report-drilldown__back-btn {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--grey-900);
  border-radius: var(--radius-4);
  background: var(--color-surface-default);
  color: var(--grey-900);
  padding: var(--space-4) var(--space-6);
  font-size: var(--font-size-xs);
  cursor: pointer;
  gap: var(--space-4);
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--grey-50);
  }
}

.count-card {
  display: flex;
  align-items: center;
  width: fit-content;
  border-radius: var(--radius-4);
  border: 1px solid var(--grey-900);
  background: var(--grey-50);
  padding: var(--space-3) var(--space-5);
}

.count-card__label {
  color: var(--grey-900);
}

.count-card__value {
  color: var(--grey-500);
}
</style>
